import { Fragment, useMemo, useState } from 'react';
import {
  ClipboardDocumentListIcon,
  ArrowDownTrayIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  PencilSquareIcon,
  CheckIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  useJobCardStockSummary,
  useJobCardStockJobs,
  useJobCardStockFaulty,
  useJobCardStockFilters,
} from '../hooks/useJobCardStock';
import { useJobCatalog, useJobEdits, useCorrectLine } from '../hooks/useJobCardStockEdit';

// What engineers logged against their job cards.
//
// Van Stock answers "what is left on the van" and counts ISP only, because
// consumables have no van baseline to reconcile against. This is the only place
// consumable usage is visible at all, so ISP and consumables are shown as two
// deliberate halves rather than one blended list.

// Dates are stored ISO and shown DD/MM/YYYY.
const fmt = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const iso = (d) => d.toISOString().slice(0, 10);
const qty = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n ?? '');
  // Civils counts ducting by the metre; installs are whole units.
  return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

function Stat({ label, value, tone }) {
  const tones = {
    default: 'bg-gray-100 text-gray-700',
    isp: 'bg-sky-100 text-sky-800',
    consumable: 'bg-amber-100 text-amber-800',
    warn: 'bg-rose-100 text-rose-800',
  };
  return (
    <span className={`rounded-full px-3 py-1 text-sm ${tones[tone] || tones.default}`}>
      {label} <span className="font-semibold">{value}</span>
    </span>
  );
}

// A status worth noticing: stock logged against a job that never completed.
const abortedStatus = (s) => /^(uti|dna)\b/i.test((s || '').trim());

function ItemTable({ rows, emptyNote }) {
  if (!rows.length) return <p className="px-4 py-6 text-center text-sm text-gray-400">{emptyNote}</p>;
  const byCategory = rows.reduce((m, r) => {
    const k = r.category || 'Uncategorised';
    (m[k] = m[k] || []).push(r);
    return m;
  }, {});
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
            <th className="px-4 py-2 font-medium">Item</th>
            <th className="px-4 py-2 font-medium">SKU</th>
            <th className="px-4 py-2 font-medium">Region</th>
            <th className="px-4 py-2 text-right font-medium">Used</th>
            <th className="px-4 py-2 text-right font-medium">Job cards</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byCategory).map(([cat, items]) => (
            <Fragment key={cat}>
              <tr className="bg-gray-50">
                <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                  {cat}
                </td>
              </tr>
              {items.map((r, i) => (
                <tr key={`${cat}-${r.sku || r.item_name}-${i}`} className="border-b border-gray-100">
                  <td className="px-4 py-2 text-gray-900">{r.item_name}</td>
                  <td className="px-4 py-2 text-xs text-gray-400">{r.sku || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{r.region || '—'}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-gray-900">{qty(r.quantity)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{r.jobs}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One line's quantity while correcting. Local state so typing doesn't refetch,
// and the tick only appears once the value actually differs.
function EditQty({ line, disabled, onSave }) {
  const [v, setV] = useState(String(line.quantity ?? ''));
  const changed = Number(v) !== Number(line.quantity);
  return (
    <span className="flex shrink-0 items-center gap-1">
      <input
        type="number" min="0" step="any" value={v}
        onChange={(e) => setV(e.target.value)}
        className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm tabular-nums"
      />
      <button
        type="button" onClick={() => onSave(Number(v))}
        disabled={disabled || !changed || v === ''}
        title={disabled ? 'Enter a reason first' : 'Save this quantity'}
        className="rounded p-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-25"
      >
        <CheckIcon className="h-4 w-4" />
      </button>
    </span>
  );
}

// Add an item the engineer never logged. The catalogue is fetched per job so
// it matches exactly what his form offered — same region, same OG/UG filter.
function AddLine({ job, disabled, onAdd }) {
  const [catalogId, setCatalogId] = useState('');
  const [q, setQ] = useState('1');
  const { data: catalog = [], isLoading } = useJobCatalog(job.discipline, job.job_id, true);
  const already = new Set(job.lines.map((l) => l.catalog_id));
  const options = catalog.filter((c) => !already.has(c.catalog_id));

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-2">
      <select
        value={catalogId} onChange={(e) => setCatalogId(e.target.value)}
        className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
      >
        <option value="">{isLoading ? 'Loading catalogue…' : 'Add an item the engineer missed…'}</option>
        {options.map((c) => (
          <option key={c.catalog_id} value={c.catalog_id}>
            {c.is_consumable ? '[consumable] ' : ''}{c.display_label || c.item_name}
          </option>
        ))}
      </select>
      <input
        type="number" min="0" step="any" value={q} onChange={(e) => setQ(e.target.value)}
        className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm tabular-nums"
      />
      <button
        type="button"
        onClick={() => { onAdd(Number(catalogId), Number(q)); setCatalogId(''); setQ('1'); }}
        disabled={disabled || !catalogId || !q}
        className="flex items-center gap-1 rounded-md bg-brand px-3 py-1 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40"
      >
        <PlusIcon className="h-4 w-4" /> Add
      </button>
    </div>
  );
}

function JobRow({ job }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const correct = useCorrectLine();
  const { data: edits } = useJobEdits(job.discipline, job.job_id, open);

  const save = (catalogId, quantity) =>
    correct.mutate({ discipline: job.discipline, jobId: job.job_id, catalogId, quantity, reason: reason.trim() });

  const isp = job.lines.filter((l) => !l.is_consumable);
  const con = job.lines.filter((l) => l.is_consumable);
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        {open ? <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              : <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{job.wo_number || '(no WO)'}</span>
            <span className="text-xs text-gray-400">{fmt(job.job_date)}</span>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
              {job.discipline}
            </span>
            {job.region ? (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">Region {job.region}</span>
            ) : null}
            {abortedStatus(job.status) ? (
              <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-800">
                {job.status}
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block truncate text-sm text-gray-500">{job.address || '—'}</span>
          <span className="mt-0.5 block text-xs text-gray-400">
            {job.engineer || 'unknown engineer'}
            {' · '}
            <TruckIcon className="mb-0.5 inline h-3.5 w-3.5" /> {job.van || 'no source van'}
            {' · '}
            {isp.length} ISP{con.length ? ` + ${con.length} consumable` : ''}
          </span>
        </span>
      </button>

      {open ? (
        <div className="bg-gray-50 px-4 pb-3 pl-11">
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-400">
              {editing ? 'Set a quantity and save. 0 removes the line.' : 'Recorded on site by the engineer.'}
            </p>
            <button
              type="button"
              onClick={() => { setEditing((e) => !e); setReason(''); correct.reset(); }}
              className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
              {editing ? 'Done' : 'Correct'}
            </button>
          </div>

          {editing ? (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <label className="block text-xs font-medium text-amber-900">
                Reason for the correction (required — kept on the record)
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. miscounted on site, forgot to log the eyebolts"
                  className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1 text-sm text-gray-900"
                />
              </label>
              {correct.isError ? (
                <p className="mt-1 text-xs text-rose-700">
                  {correct.error?.response?.data?.error || 'Could not save the correction.'}
                </p>
              ) : null}
            </div>
          ) : null}

          {[['ISP stock', isp], ['Consumables', con]].map(([label, rows]) =>
            rows.length ? (
              <div key={label} className="mt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
                <ul className="mt-1 space-y-0.5">
                  {rows.map((l, i) => (
                    <li key={`${l.sku || l.item_name}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                      <span className="min-w-0 flex-1 truncate text-gray-700">
                        {l.item_name}
                        {l.sku ? <span className="ml-2 text-xs text-gray-400">{l.sku}</span> : null}
                      </span>
                      {editing ? (
                        <EditQty
                          line={l}
                          disabled={!reason.trim() || correct.isPending}
                          onSave={(q) => save(l.catalog_id, q)}
                        />
                      ) : (
                        <span className="font-semibold tabular-nums text-gray-900">{qty(l.quantity)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}

          {editing ? <AddLine job={job} disabled={!reason.trim() || correct.isPending} onAdd={save} /> : null}

          {edits?.length ? (
            <div className="mt-3 border-t border-gray-200 pt-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Corrections</p>
              <ul className="mt-1 space-y-0.5">
                {edits.map((e, i) => (
                  <li key={i} className="text-xs text-gray-500">
                    {e.item_name}: <span className="tabular-nums">{e.old_quantity == null ? 'added' : qty(e.old_quantity)}</span>
                    {' → '}
                    <span className="tabular-nums">{e.new_quantity == null ? 'removed' : qty(e.new_quantity)}</span>
                    {' · '}{e.edited_by_name || 'office'}{' · '}{fmt(e.created_at)}
                    {e.reason ? <span className="italic"> — “{e.reason}”</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function JobCardStock() {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 6 * 86400000);
  const [range, setRange] = useState({ from: iso(weekAgo), to: iso(today) });
  // Per job card is the default view: the usual question is "what did this
  // engineer log on this address", not "what did we get through this week".
  const [tab, setTab] = useState('jobs');
  const [filters, setFilters] = useState({ region: '', van: '', engineer: '', install_type: '' });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Any change to the window or the filters puts you back on page 1 —
  // otherwise you can land on an empty page 7 of a 2-page result.
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const setRangePart = (k, v) => { setRange((r) => ({ ...r, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilters({ region: '', van: '', engineer: '', install_type: '' }); setPage(1); };
  const activeFilters = Object.values(filters).filter(Boolean).length;

  const { data: options } = useJobCardStockFilters(range);
  const { data: summary = [], isLoading, isError, error } = useJobCardStockSummary(range, filters);
  const { data: jobsData } = useJobCardStockJobs(range, filters, page, pageSize);
  const { data: faulty = [] } = useJobCardStockFaulty(range);

  // Memoised so the `|| []` fallback isn't a fresh array each render, which
  // would retrigger the aborted-jobs useMemo below on every render.
  const jobs = useMemo(() => jobsData?.jobs || [], [jobsData]);
  const total = jobsData?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const isp = useMemo(() => summary.filter((r) => !r.is_consumable), [summary]);
  const con = useMemo(() => summary.filter((r) => r.is_consumable), [summary]);
  const aborted = useMemo(() => jobs.filter((j) => abortedStatus(j.status)), [jobs]);

  const exportCsv = () => {
    const head = ['discipline', 'region', 'category', 'type', 'sku', 'item', 'quantity', 'job_cards'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = summary.map((r) => [
      r.discipline, r.region, r.category, r.is_consumable ? 'consumable' : 'ISP',
      r.sku, r.item_name, qty(r.quantity), r.jobs,
    ].map(esc).join(','));
    const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `job-card-stock-${range.from}-to-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const TABS = [
    ['jobs', `By job card (${jobs.length})`],
    ['summary', `Summary (${summary.length})`],
    ['faulty', `Faulty (${faulty.length})`],
  ];

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <ClipboardDocumentListIcon className="h-6 w-6 text-brand" /> Job Card Stock
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            What engineers logged against their job cards. Van Stock shows what is left on a van and covers ISP
            only — this is the only view that reports consumable usage.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date" value={range.from}
            onChange={(e) => setRangePart('from', e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <span className="text-sm text-gray-400">to</span>
          <input
            type="date" value={range.to}
            onChange={(e) => setRangePart('to', e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="button" onClick={exportCsv} disabled={!summary.length}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      {/* Filters. Options come from the server scoped to the period, so a van
          or engineer with nothing in range is never offered. */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={filters.region} onChange={(e) => setFilter('region', e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
          <option value="">All regions</option>
          {(options?.regions || []).map((r) => <option key={r} value={r}>Region {r}</option>)}
        </select>
        <select value={filters.install_type} onChange={(e) => setFilter('install_type', e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
          <option value="">Overhead &amp; underground</option>
          {(options?.install_types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filters.van} onChange={(e) => setFilter('van', e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
          <option value="">All vans</option>
          {(options?.vans || []).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filters.engineer} onChange={(e) => setFilter('engineer', e.target.value)}
          className="min-w-[10rem] rounded-md border border-gray-300 px-2 py-1.5 text-sm">
          <option value="">All engineers</option>
          {(options?.engineers || []).map((e2) => <option key={e2.id} value={e2.id}>{e2.name}</option>)}
        </select>
        {activeFilters ? (
          <button type="button" onClick={clearFilters}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
            Clear {activeFilters} filter{activeFilters === 1 ? '' : 's'}
          </button>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Stat label="Job cards" value={total} />
        <Stat label="ISP items" value={isp.length} tone="isp" />
        <Stat label="Consumable items" value={con.length} tone="consumable" />
        {faulty.length ? <Stat label="Faulty" value={faulty.length} tone="warn" /> : null}
        {aborted.length ? <Stat label="Stock on UTI/DNA jobs" value={aborted.length} tone="warn" /> : null}
      </div>

      {aborted.length ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {aborted.length} job card{aborted.length === 1 ? '' : 's'} with stock logged against a UTI or DNA
            status — materials were used on a job that did not complete. Worth a look.
          </span>
        </p>
      ) : null}

      <div className="mt-4 flex gap-2 border-b border-gray-200">
        {TABS.map(([k, label]) => (
          <button
            key={k} type="button" onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === k ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? <p className="mt-6 text-sm text-gray-400">Loading…</p> : null}
      {isError ? (
        <p className="mt-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load job card stock.'}
        </p>
      ) : null}

      {!isLoading && !isError && tab === 'summary' ? (
        <div className="mt-4 space-y-4">
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <h2 className="border-b border-gray-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-900">
              ISP stock <span className="font-normal text-sky-700">— also reconciled against the van on Van Stock</span>
            </h2>
            <ItemTable rows={isp} emptyNote="No ISP stock logged in this period." />
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <h2 className="border-b border-gray-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900">
              Consumables <span className="font-normal text-amber-700">— recorded here only; no van balance yet</span>
            </h2>
            <ItemTable rows={con} emptyNote="No consumables logged in this period." />
          </div>
        </div>
      ) : null}

      {!isLoading && !isError && tab === 'jobs' ? (
        <>
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            {jobs.length ? jobs.map((j) => <JobRow key={`${j.discipline}-${j.job_id}`} job={j} />)
              : (
                <p className="px-4 py-8 text-center text-sm text-gray-400">
                  {activeFilters ? 'No job cards match these filters.' : 'No job cards with stock in this period.'}
                </p>
              )}
          </div>

          {total > pageSize ? (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} job cards
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  Previous
                </button>
                <span className="text-sm tabular-nums text-gray-500">{page} / {pages}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {!isLoading && !isError && tab === 'faulty' ? (
        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {faulty.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">WO</th>
                  <th className="px-4 py-2 font-medium">Engineer</th>
                  <th className="px-4 py-2 font-medium">Item</th>
                  <th className="px-4 py-2 text-right font-medium">Qty</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {faulty.map((f, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-4 py-2 whitespace-nowrap text-gray-500">{fmt(f.job_date)}</td>
                    <td className="px-4 py-2 text-gray-900">{f.wo_number || '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{f.engineer || '—'}</td>
                    <td className="px-4 py-2 text-gray-900">{f.item_name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{qty(f.quantity)}</td>
                    <td className="px-4 py-2 text-gray-500">{f.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="px-4 py-8 text-center text-sm text-gray-400">No faulty or damaged items reported in this period.</p>}
        </div>
      ) : null}
    </div>
  );
}

export default JobCardStock;
